import { useSessionStorage } from 'usehooks-ts'
import { Box, Card, CardContent, CardMedia, Typography } from '@mui/material'
import { HistoryToggleOff } from '@mui/icons-material'

import OneWaySlabHistoryItem, { OneWaySlabHistory } from '@page/one-way-slab/OneWaySlabHistoryItem'
import TwoWaySlabHistoryItem, { TwoWaySlabHistory } from '@page/two-way-slab/TwoWaySlabHistoryItem'
import BeamHistoryItem, { BeamHistory } from '@page/beam/BeamHistoryItem'
import ColumnHistoryItem, { ColumnHistory } from '@page/column/ColumnHistoryItem'
import FoundationHistoryItem, { FoundationHistory } from '@page/foundation/FoundationHistoryItem'
import BasementWallHistoryItem, { BasementWallHistory } from '@page/basement-wall/BasementWallHistoryItem'
import WindTunnelStudyHistoryItem, { WindTunnelStudyHistory } from '@page/wind-tunnel-study/WindTunnelStudyHistoryItem'
import { useTranslation } from 'react-i18next'

type CalculateHistory = (
  OneWaySlabHistory
  | TwoWaySlabHistory
  | BeamHistory
  | ColumnHistory
  | FoundationHistory
  | BasementWallHistory
  | WindTunnelStudyHistory
)

export const useHistoryList = () => {
  const [
    historyList,
    setHistoryList,
  ] = useSessionStorage<CalculateHistory[]>('history', [])

  return {
    historyList,
    appendHistory (history: CalculateHistory) {
      setHistoryList([history, ...historyList])
    },
    clearHistoryList () {
      setHistoryList([])
    },
  }
}

function HistoryList () {
  const { t } = useTranslation()
  const { historyList } = useHistoryList()
  const isEmpty = historyList.length === 0

  const renderHistoryItem = (_: CalculateHistory, i: number) => {
    switch (_.type) {
      case 'OneWaySlab':
        return <OneWaySlabHistoryItem history={_} key={i} />
      case 'TwoWaySlab':
        return <TwoWaySlabHistoryItem history={_} key={i} />
      case 'Beam':
        return <BeamHistoryItem history={_} key={i} />
      case 'Column':
        return <ColumnHistoryItem history={_} key={i} />
      case 'Foundation':
        return <FoundationHistoryItem history={_} key={i} />
      case 'BasementWall':
        return <BasementWallHistoryItem history={_} key={i} />
      case 'WindTunnelStudy':
        return <WindTunnelStudyHistoryItem history={_} key={i} />
      default:
    }

    return undefined
  }

  return (
    <Card className="flex-1 min-w-[44rem] my-8">
      <CardMedia
        component="img"
        className="object-cover aspect-[3/1] bg-gray-700"
        image="https://picsum.photos/1080/560"
        alt="banner"
      />
      {isEmpty && (
        <CardContent className="h-full flex flex-col gap-8 items-center justify-center">
          <HistoryToggleOff fontSize="large" color="info" />
          <Typography variant="subtitle1" className="!text-gray-400">
            {t('COMPOUND.EMPTY_HISTORY')}
          </Typography>
        </CardContent>
      )}
      <CardContent className="overflow-auto my-12">
        <Box className="flex flex-col gap-8">
          {historyList.map(renderHistoryItem)}
        </Box>
      </CardContent>
    </Card>
  )
}

export default HistoryList
